import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useMatch, useNavigate, useParams, useLocation } from "react-router-dom"
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber } from 'react-phone-number-input'
import { validateAddEditForm } from './validations';
import Tabs from "./components/Tabs";
import UserDetailsTab from "./components/UserDetailsTab";
import PropertiesTab from './components/PropertiesTab';
import AssignPropertiesModal from './components/AssignPropertiesModal';
import Header from './components/Header';
import Buttons from './components/Buttons';
import PermissionMetricsModal from './components/PermissionMetricsModal';
import useFetch from '../../../../hooks/useFetch';
import useUnsavedChangesWarning from '../../../../hooks/useUnsavedChangesWarning';
import ConfirmationFormRouting from '../../../../components/ConfirmationFormRouting';
import Button from "../../../../components/Button";
import Modal from '../../../../components/Modal';
import { VALIDATION_FIELD_MAPPING, APP_SUCCESS_MESSAGES, MaxLength } from "../../../../utils/appConstant"
import { AddEdit as AddEditInterface, AddEditApi } from '../../../../interfaces/user.interface';
import { Validation } from '../../../../interfaces/common';
import UserService from '../../../../services/user.service';
import RoleService from '../../../../services/role.service';
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';
import Spinner from '../../../../components/Spinner';
import Toast from '../../../../components/Toast';
import { STATUS_CODE } from '../../../../utils/apiConstant';

const initialFormData: AddEditInterface = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  manager: '',
  phoneNumber: '',
  countryCode: '',
  notes: '',
  status: true,
}

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  manager: '',
  role: '',
}

const AddEditView: React.FC = () => {
  const location = useLocation()
  const { t } = useTranslation();
  const { id } = useParams();
  const view = !!useMatch(Modules.resources + AuthRoutes.users + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.resources + AuthRoutes.users + RouteParamsTypes.edit + '/:id');
  const navigate = useNavigate()

  const [tab, setTab] = useState<'userDetails' | 'properties'>('userDetails');
  const [formData, setFormData] = useState<AddEditInterface>(initialFormData);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [loading, setLoading] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState<boolean>(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(0);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [removalId, setRemovalId] = useState<string>('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [permissionMetricsModalOpen, setPermissionMetricsModalOpen] = useState<boolean>(false);
  const currentIdRef = useRef<string | undefined>(id);

  const [shouldBlock, setShouldBlock] = useState(!view);

  // Unsaved changes warning - only for add/edit, not view mode
  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChangesWarning({
    when: shouldBlock,
  });



  useEffect(() => {
    setTab(location?.state?.tab ?? 'userDetails');
  }, [location.state, location.key]);

  const { data: properties } = useFetch({
    service: UserService,
    func: 'userProperties',
    payload: {
      id: id,
      page: 1,
      perPage: MaxLength.pageSize,
      is_active: '1'
    },
    dependency: [id, counter],
    skip: !id
  });


  const { data: allRolesLookup } = useFetch({
    service: RoleService,
    func: 'lookup',
    payload: {
      searchText: ''
    },
    dependency: []
  });

  const { data: allManagersLookup } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: {
      searchText: '', ...(!edit && !view && { is_active: '1' })
    },
    dependency: [location.key]
  });

  const { data: roles } = useFetch({
    service: RoleService,
    func: 'lookup',
    payload: {
      searchText: dropdownSearchData.role
    },
    dependency: [dropdownSearchData.role]
  });

  const { data: managers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: {
      searchText: dropdownSearchData.manager, ...(!edit && !view && { is_active: '1' })
    },
    dependency: [dropdownSearchData.manager, location.key]
  });

  useEffect(() => {
    currentIdRef.current = id;
    setFormData(initialFormData)
    setDropdownSearchData(initialDropdownSearchData)
    setValidation(initialValidation)
    setShouldBlock(!view);
  }, [location.key, id, view]);

  const { loading: getContentLoading } = useFetch({
    service: UserService,
    func: 'get',
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: async (response: AddEditApi) => {
      if (!currentIdRef.current) return;

      setFormData({
        firstName: response.first_name,
        lastName: response.last_name,
        email: response.email,
        role: response.role,
        manager: response.manager_id,
        phoneNumber: response.phone_number,
        countryCode: response.country_code || "",
        notes: response.notes,
        status: response.is_active,
      })
    },
    onError: (error: any) => {
      if (error.code === STATUS_CODE.notFound) navigate(Modules.resources + AuthRoutes.users);
    }
  });

  const handleTabChange = useCallback((e: any, value: 'userDetails' | 'properties') => {
    setTab(value);
  }, []);

  const handleInputChange = useCallback((event: any, name: string) => {
    if (name === VALIDATION_FIELD_MAPPING.phoneNumber) {
      const phoneNumber = parsePhoneNumber(event?.target?.value ?? "")

      setFormData((prev) => {
        return {
          ...prev,
          [VALIDATION_FIELD_MAPPING.phoneNumber]: phoneNumber && phoneNumber.nationalNumber,
          [VALIDATION_FIELD_MAPPING.countryCode]: phoneNumber ? `+${phoneNumber && phoneNumber.countryCallingCode}` : ""
        };
      });
    } else {
      let value: any;

      if (name === VALIDATION_FIELD_MAPPING.status) {
        value = event.target.checked
      } else {
        value = event.target.value
      }

      setFormData((prev) => {
        return {
          ...prev,
          [name]: value
        };
      });
    }
  }, [setFormData]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setDropdownSearchData]);

  const handleInputBlur = useCallback((event: any, name: string) => {
    let newValidation = validateAddEditForm({
      ...formData,
      [name]: event.target.value.trim(),
    }, name);

    setValidation(prev => {
      const filteredFields = prev.fields.filter(f => f.field !== name);
      const mergedFields = [...filteredFields, ...newValidation.fields];

      return {
        validate: mergedFields.length === 0,
        fields: mergedFields,
      };
    });
  }, [formData]);

  const handleSubmit = useCallback(async (action: '1' | '2', event?: React.FormEvent,) => {
    event && event.preventDefault();

    const newValidation = validateAddEditForm(formData);
    setValidation(newValidation);
    if (!newValidation.validate) return;

    if (action === '1') setLoading(true);
    else setActionTwoLoading(true);

    try {
      const payload: AddEditApi = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        manager_id: formData.manager,
        role_id: formData.role,
        phone_number: formData.phoneNumber && formData.phoneNumber.trim(),
        country_code: formData.countryCode && formData.countryCode.trim(),
        notes: formData.notes?.trim(),
        is_active: formData.status
      }

      if (!edit) { payload.email = formData.email && formData.email.trim() }

      let response: any
      if (!edit) {
        response = await UserService.add(payload);
      } else if (id) {
        response = await UserService.edit(id, payload);
      }

      if (response.success !== false) {
        toast.success(<Toast message={edit ? APP_SUCCESS_MESSAGES.userUpdated : APP_SUCCESS_MESSAGES.userCreated} />, { icon: false });

        setShouldBlock(false);
        setTimeout(() => {
          if (edit) {
            navigate(Modules.resources + AuthRoutes.users);
          } else if (action === '2') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const input = document.getElementById(VALIDATION_FIELD_MAPPING.firstName);
            input && input.focus();
            navigate(Modules.resources + AuthRoutes.users + RouteParamsTypes.add, { replace: true });
            // setShouldBlock(true);
          } else {
            const createdId = response?.id;
            navigate(`${Modules.resources}${AuthRoutes.users}${RouteParamsTypes.edit}/${createdId}`, { state: { tab: 'properties' }, replace: true });
          }
        }, 0);
      }

      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    } catch (err: any) {
      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    }
  }, [id, edit, formData, navigate]);

  const handleKeyDown = useCallback((e: any, name?: string) => {
    if (name === VALIDATION_FIELD_MAPPING.notes) return;
    if (e.key === 'Enter') handleSubmit('1');
  }, [handleSubmit]);

  const handleCancel = () => {
    setShouldBlock(false);
    setTimeout(() => {
      navigate(Modules.resources + AuthRoutes.users);
    }, 0);
  }

  const handleAssignProperty = useCallback(async (propertyIds: string) => {
    setAssignmentLoading(true);

    try {
      await UserService.bulkAssignProperties(id || '', propertyIds.split(','));

      setAssignmentLoading(false);
      setShowAssignModal(false);
      setCounter((prev) => prev + 1);
      toast.success(<Toast message={APP_SUCCESS_MESSAGES.propertyAssigned} />, { icon: false });
    } catch (e) {
      setAssignmentLoading(false);
    }
  }, [id]);

  const handleRemoveProperty = useCallback(async (assignmentId: string) => {
    try {
      await UserService.removeProperty(assignmentId);
      setCounter((prev) => prev + 1);
      setRemovalId('');
    } catch (e) {
    }
  }, []);

  const handleRemovePropertyClick = useCallback((assignmentId: string) => {
    setRemovalId(assignmentId)
  }, [])

  const modalAction = (
    <>
      <Button
        className="btn btn-outline-secondary me-3"
        text={t("users.addEdit.pageTexts.form.confirmModal.cancelBtn")}
        onClick={e => setRemovalId('')}
      />
      <Button
        className="btn btn-danger me-3"
        text={t("users.addEdit.pageTexts.form.confirmModal.removeBtn")}
        onClick={e => handleRemoveProperty(removalId)}
      />
    </>
  )

  let formContent;

  if (getContentLoading) {
    formContent = (
      <div className='card-header text-center'>
        <Spinner />
      </div>
    )
  } else if (tab === 'userDetails') {
    formContent = (
      <UserDetailsTab
        edit={edit}
        view={view}
        allRoles={allRolesLookup}
        allManagers={allManagersLookup}
        managers={managers}
        formData={formData}
        roles={roles}
        validation={validation}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onKeyDown={handleKeyDown}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
        onPermissionMetricsClick={() => setPermissionMetricsModalOpen(true)}
      />
    )
  } else if (tab === 'properties') {
    formContent = (
      <PropertiesTab
        properties={properties}
        view={view}
        edit={edit}
        onAssignPropertiesClick={() => setShowAssignModal(true)}
        onRemoveProperty={handleRemovePropertyClick}
      />
    )
  }

  return (
    <>
      <Header id={id} view={view} edit={edit} loading={getContentLoading} />

      <div className="card mb-6">
        <h5 className="card-header">{!id ? t("users.addEdit.pageTexts.title.addTitle") : (view ? t("users.addEdit.pageTexts.title.viewTitle") : t("users.addEdit.pageTexts.title.editTitle"))}</h5>

        <hr className="m-0"></hr>

        <Tabs tab={tab} add={!edit && !view} onTabChange={handleTabChange} />

        <hr className="m-0"></hr>

        {formContent}

        <hr className="m-0"></hr>

        <Buttons
          tab={tab}
          actionTwoLoading={actionTwoLoading}
          loading={loading}
          view={view}
          edit={edit}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>

      <Modal
        open={removalId ? true : false}
        onClose={() => setRemovalId('')}
        title={t("users.addEdit.pageTexts.form.confirmModal.title")}
        content={t("users.addEdit.pageTexts.form.confirmModal.content")}
        action={modalAction}
        contentClassName="text-center"
        maxWidth="sm"
      />

      <PermissionMetricsModal
        open={permissionMetricsModalOpen}
        onClose={() => setPermissionMetricsModalOpen(false)}
      />

      <AssignPropertiesModal
        open={showAssignModal}
        userId={id}
        loading={assignmentLoading}
        defaultAssignedTo={formData.manager}
        onClose={() => setShowAssignModal(false)}
        onAssign={handleAssignProperty}
      />

      {/* Unsaved Changes Confirmation Modal */}
      <ConfirmationFormRouting
        open={showPrompt}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
      />
    </>
  );
};

export default AddEditView;
